import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import HtmlContentFromApi from '@r1-frontend/ui-react/components/HtmlContentFromApi';
import { Close } from '@r1-frontend/ui-react/components/svg/main';

import { useAnalytics } from '~/src/features/full-buy/analytics/context';
import useCheckPromoCode from '~/src/features/full-buy/PromoCode/hooks/checkPromoCode';
import { setPromoCode } from '~/src/features/full-buy/store/actions/setPromoCode';
import { usePrepareQueryData } from '~/src/helpers/hooks/usePrepareQueryData';
import { TState } from '~/src/store';
import { selectDomain } from '~/src/store/selectors/domain';
import selectProviderId from '~/src/store/selectors/selectProviderId';

import * as ST from './styled';

export enum EBonusTypes {
    AMOUNT = 0,
    PERCENT = 1,
}

interface IProps {
    hasAgreement: boolean,
}

const PromoCode = ({ hasAgreement }: IProps) => {
    const analytics = useAnalytics();

    const domain = useSelector((state: TState) => selectDomain(state));
    const { providerId, bonusAmount, bonusType } = useSelector((state: TState) => ({
        providerId: selectProviderId(state),
        bonusAmount: state.fullBuy.promoCode?.amount,
        bonusType: state.fullBuy.promoCode?.type,
    }));
    const [promoCodeValue, setPromoCodeValue] = useState<string>('');
    const [startInput, setStartInput] = useState(true);

    const { code } = usePrepareQueryData();

    const dispatch = useDispatch();
    const { processCheck, isError, errorMessage, isLoading, clearErrors } = useCheckPromoCode(providerId, domain, analytics, (data) => {
        analytics.summary.promocodeSuccessVerification(data.value);
        dispatch(setPromoCode(data));
    });

    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (startInput) {
            analytics.summary.promocodeStartedTyping();
            setStartInput(false);
        }
        setPromoCodeValue(e.target.value);
        if (e.target.value === '') {
            clearErrors();
        }
    };

    useEffect(() => {
        if (code && !promoCodeValue) {
            setPromoCodeValue(code);
            processCheck(code);
        }
    }, []);

    const onCheckCode = useCallback(async() => {
        processCheck(promoCodeValue);
    }, [promoCodeValue]);

    const bonusMessage = useMemo(() => {
        return bonusType === EBonusTypes.AMOUNT
            ? `Вам будет начислено <span>${bonusAmount} бонусов</span>.<br /> Мы зарегистрируем вас в программе лояльности, чтобы вы могли ими воспользоваться.`
            : `<span>${bonusAmount}%</span> от абонентской платы будет начислено бонусами. Мы зарегистрируем вас в программе лояльности, чтобы вы могли ими воспользоваться`;
    }, [bonusAmount, bonusType]);

    const onClickClear = useCallback(() => {
        dispatch(setPromoCode(null));
        setPromoCodeValue('');
        clearErrors();
    }, [promoCodeValue]);

    const onInputCLick = useCallback(() => {
        analytics.summary.promocodeClickedOnHave();
    }, []);

    return (
        <ST.PromoWrapper>
            {bonusAmount
                ? <ST.ResultLine>
                    <ST.Icon />
                    <ST.Text>
                        <HtmlContentFromApi>
                            {bonusMessage}
                        </HtmlContentFromApi>
                    </ST.Text>
                    {!hasAgreement && <ST.StyledButton Icon={<Close />} bType={'ghost'} size={'small'} onClick={onClickClear}/>}
                </ST.ResultLine>
                : !hasAgreement
                    ? <ST.FormLine>
                        <ST.StyledInputSimple
                            placeholder={'Есть промокод?'}
                            value={promoCodeValue}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e)}
                            onClick={onInputCLick}
                            errorMessage={isError ? errorMessage : null}
                            isNotValid={isError}
                            disabled={isLoading}
                        />
                        <Button
                            bType={'secondary'}
                            onClick={onCheckCode}
                            disabled={isLoading || !promoCodeValue}
                        >Проверить</Button>
                    </ST.FormLine>
                    : <></>
            }
        </ST.PromoWrapper>
    );
};

export default PromoCode;
